import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Store, ShieldCheck, Loader2, CheckCircle2, AlertCircle, Mail, User as UserIcon, ArrowRight, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
// ─── Roles ───────────────────────────────────────────────────────────
// Coincide con el set permitido por restaurant_users.role.
// Cada rol se mantiene tal cual está hoy en el resto del sistema;
// este módulo SOLO asigna la fila, no inventa permisos nuevos.
const ASSIGNABLE_ROLES = [{
  value: 'admin',
  label: 'Admin de restaurante',
  desc: 'Acceso completo al admin del restaurante (menú, mesas, métricas).'
}, {
  value: 'kds',
  label: 'KDS',
  desc: 'Acceso a la pantalla de cocina.'
}, {
  value: 'cocina',
  label: 'Cocina',
  desc: 'Estación de cocina dentro del KDS.'
}, {
  value: 'bebidas',
  label: 'Bebidas',
  desc: 'Estación de bebidas dentro del KDS.'
}, {
  value: 'pizzeria',
  label: 'Pizzería',
  desc: 'Estación de pizzería dentro del KDS.'
}, {
  value: 'caja',
  label: 'Caja',
  desc: 'Vista de caja / cobros.'
}] as const;
type RoleValue = (typeof ASSIGNABLE_ROLES)[number]['value'];
interface UserRow {
  user_id: string;
  email: string;
  full_name: string | null;
  has_restaurant_access: boolean;
  restaurant_access_count: number | null;
  restaurant_roles: string[] | null;
  profile_is_active: boolean;
}
interface RestaurantRow {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
}
const inputClass = 'w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-base text-white placeholder:text-white/25 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847] transition-colors';
const labelClass = 'block text-xs font-bold text-white/40 uppercase tracking-widest mb-2';
export function EnrollmentRestaurantAdminPage() {
  const navigate = useNavigate();
  // ─── Guard state ───────────────────────────────────────────────────
  const [guardState, setGuardState] = useState<'loading' | 'authorized' | 'redirecting'>('loading');
  // ─── Form state ────────────────────────────────────────────────────
  const [searchMode, setSearchMode] = useState<'email' | 'uuid'>('email');
  const [emailQuery, setEmailQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [searchDebug, setSearchDebug] = useState<{
    rpcSucceeded: boolean;
    rpcErrorMessage: string | null;
    rpcRowCount: number;
    viewErrorMessage: string | null;
    viewRowCount: number;
    totalUnique: number;
  } | null>(null);
  // UUID paste flow — siempre funciona, no depende de RPCs ni vistas.
  const [manualUserId, setManualUserId] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualChecking, setManualChecking] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const [restaurants, setRestaurants] = useState<RestaurantRow[]>([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');
  const [role, setRole] = useState<RoleValue>('admin');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    email: string;
    restaurantName: string;
    role: string;
  } | null>(null);
  // ─── Access guard — only platform_admin / super_admin ──────────────
  useEffect(() => {
    let cancelled = false;
    async function guard() {
      try {
        const {
          data: {
            user
          }
        } = await supabase.auth.getUser();
        if (!user) {
          if (!cancelled) setGuardState('redirecting');
          navigate('/admin/login', {
            replace: true
          });
          return;
        }
        // platform_admins is the primary table for marketplace-level roles.
        const {
          data: platformData
        } = await supabase.from('platform_admins').select('role').eq('user_id', user.id).eq('is_active', true).maybeSingle();
        let ok = platformData && (platformData.role === 'super_admin' || platformData.role === 'platform_admin');
        if (!ok) {
          // Fallback: restaurant_users may carry a platform-level role too.
          const {
            data: ruData
          } = await supabase.from('restaurant_users').select('role').eq('user_id', user.id).eq('is_active', true).in('role', ['super_admin', 'platform_admin']).limit(1).maybeSingle();
          ok = !!ruData;
        }
        if (cancelled) return;
        if (!ok) {
          setGuardState('redirecting');
          navigate('/', {
            replace: true
          });
          return;
        }
        setGuardState('authorized');
      } catch (err) {
        console.error('[EnrollmentRestaurantAdmin] guard error:', err);
        if (!cancelled) {
          setGuardState('redirecting');
          navigate('/admin/login', {
            replace: true
          });
        }
      }
    }
    guard();
    return () => {
      cancelled = true;
    };
  }, [navigate]);
  // ─── Load restaurants ──────────────────────────────────────────────
  useEffect(() => {
    if (guardState !== 'authorized') return;
    let cancelled = false;
    (async () => {
      setRestaurantsLoading(true);
      const {
        data,
        error
      } = await supabase.from('restaurants').select('id, name, slug, is_active').eq('is_active', true).order('name', {
        ascending: true
      });
      if (cancelled) return;
      if (error) {
        console.error('[EnrollmentRestaurantAdmin] restaurants error:', error);
      }
      setRestaurants((data || []) as RestaurantRow[]);
      setRestaurantsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [guardState]);
  // ─── Debounced user search ─────────────────────────────────────────
  // Estrategia: combinar DOS fuentes y deduplicar por user_id.
  //   1. RPC `find_auth_user_by_email` → busca DIRECTO en auth.users.
  //      Incluye usuarios recién creados que aún no tienen ninguna fila
  //      en restaurant_users / platform_admins / profiles.
  //   2. Vista `v_user_access_summary` → usuarios que ya tienen algún
  //      acceso. Es el fallback si la RPC no está desplegada todavía
  //      (correr CREATE_FIND_AUTH_USER_RPC.sql en Supabase).
  useEffect(() => {
    if (guardState !== 'authorized') return;
    const q = emailQuery.trim();
    setSearchDebug(null);
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      const byId = new Map<string, UserRow>();
      let rpcSucceeded = false;
      let rpcErrorMessage: string | null = null;
      let rpcRowCount = 0;
      let viewErrorMessage: string | null = null;
      let viewRowCount = 0;
      // ── 1. RPC contra auth.users ───────────────────────────────────
      try {
        const {
          data: rpcData,
          error: rpcError
        } = await supabase.rpc('find_auth_user_by_email', {
          search_query: q
        });
        if (rpcError) {
          rpcErrorMessage = rpcError.message || 'RPC find_auth_user_by_email no disponible';
          console.warn('[EnrollmentRestaurantAdmin] RPC error:', rpcError);
        } else {
          rpcSucceeded = true;
          if (rpcData) {
            for (const row of rpcData as any[]) {
              if (!row?.user_id) continue;
              rpcRowCount++;
              byId.set(String(row.user_id), {
                user_id: row.user_id,
                email: row.email || '',
                full_name: row.full_name ?? null,
                has_restaurant_access: row.has_restaurant_access === true,
                restaurant_access_count: null,
                restaurant_roles: null,
                profile_is_active: true
              });
            }
          }
        }
      } catch (err: any) {
        rpcErrorMessage = err?.message || 'RPC exception';
        console.warn('[EnrollmentRestaurantAdmin] RPC exception:', err);
      }
      if (cancelled) return;
      // ── 2. Vista v_user_access_summary (complemento) ───────────────
      try {
        const {
          data,
          error
        } = await supabase.from('v_user_access_summary').select('user_id, email, full_name, has_restaurant_access, restaurant_access_count, restaurant_roles, profile_is_active').or(`email.ilike.%${q}%,full_name.ilike.%${q}%`).limit(15);
        if (cancelled) return;
        if (error) {
          viewErrorMessage = error.message;
          console.warn('[EnrollmentRestaurantAdmin] v_user_access_summary error:', error);
        } else if (data) {
          for (const row of data as any[]) {
            if (!row?.user_id) continue;
            viewRowCount++;
            if (!byId.has(String(row.user_id))) {
              byId.set(String(row.user_id), row as UserRow);
            }
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          viewErrorMessage = err?.message || 'view exception';
          console.warn('[EnrollmentRestaurantAdmin] view search exception:', err);
        }
      }
      if (cancelled) return;
      setSearchResults(Array.from(byId.values()));
      setSearchDebug({
        rpcSucceeded,
        rpcErrorMessage,
        rpcRowCount,
        viewErrorMessage,
        viewRowCount,
        totalUnique: byId.size
      });
      setSearching(false);
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [emailQuery, guardState]);
  // ─── Manual UUID lookup ────────────────────────────────────────────
  // Validate format + check restaurant_users for existing assignment.
  // Even if the user_id doesn't exist in auth.users, the final insert
  // will fail on FK and we'll show that error cleanly.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const handleManualConfirm = async () => {
    setManualError(null);
    const id = manualUserId.trim().toLowerCase();
    if (!UUID_RE.test(id)) {
      setManualError('El User ID no tiene formato de UUID. Copialo desde Supabase Auth → Users.');
      return;
    }
    setManualChecking(true);
    try {
      const {
        data,
        error
      } = await supabase.from('restaurant_users').select('id, restaurant_id, role, is_active').eq('user_id', id).eq('is_active', true).limit(1);
      if (error) {
        // No es fatal — quizá RLS bloquea SELECT. Lo dejamos pasar y
        // confiamos en el chequeo del submit.
        console.warn('[EnrollmentRestaurantAdmin] manual restaurant_users check error:', error);
      }
      const alreadyAssigned = !!(data && data.length > 0);
      setSelectedUser({
        user_id: id,
        email: manualEmail.trim() || '(sin email)',
        full_name: null,
        has_restaurant_access: alreadyAssigned,
        restaurant_access_count: alreadyAssigned ? 1 : 0,
        restaurant_roles: null,
        profile_is_active: true
      });
    } catch (err: any) {
      setManualError(err?.message || 'Error inesperado al verificar el UUID.');
    } finally {
      setManualChecking(false);
    }
  };
  const handleSelectUser = (u: UserRow) => {
    setSelectedUser(u);
    setEmailQuery(u.email);
    setSearchResults([]);
    setError(null);
  };
  const handleClearUser = () => {
    setSelectedUser(null);
    setEmailQuery('');
    setSearchResults([]);
    setManualUserId('');
    setManualEmail('');
    setManualError(null);
    setError(null);
  };
  const selectedRestaurant = useMemo(() => restaurants.find(r => r.id === selectedRestaurantId) || null, [restaurants, selectedRestaurantId]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!selectedUser) {
      setError('Seleccioná un usuario existente de Supabase Auth.');
      return;
    }
    if (!selectedRestaurantId) {
      setError('Seleccioná un restaurante.');
      return;
    }
    if (!role) {
      setError('Seleccioná un rol.');
      return;
    }
    // Hard guard: cada usuario sólo puede estar en UN restaurante.
    // Intentamos chequear contra restaurant_users; si por RLS no
    // podemos leer (algunos roles no tienen permiso de SELECT global),
    // NO bloqueamos — confiamos en el INSERT que abajo va a fallar
    // limpiamente con duplicate key si ya está asignado.
    setIsSubmitting(true);
    try {
      const {
        data: existing,
        error: existingErr
      } = await supabase.from('restaurant_users').select('id, restaurant_id, role, is_active').eq('user_id', selectedUser.user_id).eq('is_active', true).limit(1);
      if (existingErr) {
        // SOLO logueamos. No abortamos — el INSERT siguiente atrapa el
        // duplicado por unique constraint y mostramos el error correcto.
        console.warn('[EnrollmentRestaurantAdmin] pre-check failed (continuing):', existingErr.message);
      } else if (existing && existing.length > 0) {
        setError('Este usuario ya está asignado a un restaurante.');
        setIsSubmitting(false);
        return;
      }
      // Insertar la asignación.
      const {
        error: insertErr
      } = await supabase.from('restaurant_users').insert({
        user_id: selectedUser.user_id,
        restaurant_id: selectedRestaurantId,
        role,
        is_active: true
      });
      if (insertErr) {
        console.error('[EnrollmentRestaurantAdmin] insert error:', insertErr);
        const msg = String(insertErr.message || '').toLowerCase();
        // Duplicado: unique constraint por user_id.
        if (msg.includes('duplicate') || msg.includes('unique') || msg.includes('already exists')) {
          setError('Este usuario ya está asignado a un restaurante.');
        }
        // RLS bloqueó el insert porque falta la policy de INSERT para
        // platform_admins. Es un problema de configuración de Supabase
        // que se arregla corriendo FIX_RESTAURANT_USERS_INSERT_RLS.sql
        // UNA sola vez. Se lo decimos al usuario sin tecnicismos.
        else if (msg.includes('row-level security') || msg.includes('row level security') || msg.includes('violates row-level') || msg.includes('new row violates')) {
          setError('Permiso denegado por Supabase (RLS). Correr UNA vez el archivo FIX_RESTAURANT_USERS_INSERT_RLS.sql en el SQL Editor de Supabase para habilitar a los platform admins a asignar usuarios. Después de eso este módulo va a funcionar permanentemente sin más SQL.');
        }
        // FK violation: el user_id pegado no existe en auth.users.
        else if (msg.includes('foreign key') || msg.includes('violates foreign key') || msg.includes('not present in table')) {
          setError('El User ID no existe en Supabase Auth. Verificá que esté bien copiado desde Authentication → Users.');
        } else {
          setError(`No se pudo asignar: ${insertErr.message}`);
        }
        setIsSubmitting(false);
        return;
      }
      setSuccess({
        email: selectedUser.email,
        restaurantName: selectedRestaurant?.name || '—',
        role
      });
      // Reset form state for the next assignment.
      setSelectedUser(null);
      setEmailQuery('');
      setSelectedRestaurantId('');
      setRole('admin');
    } catch (err: any) {
      console.error('[EnrollmentRestaurantAdmin] submit exception:', err);
      setError(err?.message || 'Error inesperado al asignar el usuario.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (guardState !== 'authorized') {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-4258">
        <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-4259" />
      </div>;
  }
  const userAlreadyAssigned = selectedUser && selectedUser.has_restaurant_access === true;
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-4260">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-8" data-id="element-4261">
        {/* ─── Header ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-4" data-id="element-4262">
          <button onClick={() => navigate('/admin/enrollment')} className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm font-medium transition-colors" data-id="element-4263">
            <ArrowLeft className="w-4 h-4" data-id="element-4264" />
            Volver a enrollment
          </button>
        </div>

        <div className="flex items-start gap-4" data-id="element-4265">
          <div className="w-14 h-14 bg-indigo-500/15 border border-indigo-500/20 rounded-2xl flex items-center justify-center shrink-0" data-id="element-4266">
            <ShieldCheck className="w-7 h-7 text-indigo-400" data-id="element-4267" />
          </div>
          <div data-id="element-4268">
            <h1 className="text-3xl font-black tracking-tight" data-id="element-4269">
              Admin de Restaurante
            </h1>
            <p className="text-white/50 text-sm mt-1 leading-relaxed" data-id="element-4270">
              Asignar un usuario existente de Supabase Auth como administrador o
              miembro de KDS para un solo restaurante.
            </p>
            <p className="text-amber-300/80 text-xs mt-3 leading-relaxed bg-amber-500/5 border border-amber-500/15 rounded-xl px-3 py-2" data-id="element-4271">
              Este módulo NO crea usuarios — el usuario ya debe existir en
              Supabase Auth. Cada persona puede estar asignada a un solo
              restaurante.
            </p>
          </div>
        </div>

        {/* ─── Success banner ──────────────────────────────────────── */}
        {success && <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl px-5 py-4 flex items-start gap-3" data-id="element-4272">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" data-id="element-4273" />
            <div className="flex-1" data-id="element-4274">
              <p className="text-emerald-300 font-bold text-sm" data-id="element-4275">
                Usuario asignado correctamente
              </p>
              <p className="text-emerald-200/80 text-xs mt-1" data-id="element-4276">
                <span className="font-mono" data-id="element-4277">{success.email}</span> →{' '}
                <span className="font-bold" data-id="element-4278">{success.restaurantName}</span> ·{' '}
                rol <span className="font-bold" data-id="element-4279">{success.role}</span>
              </p>
            </div>
            <button onClick={() => setSuccess(null)} className="text-emerald-300/60 hover:text-emerald-200 transition-colors" data-id="element-4280">
              <X className="w-4 h-4" data-id="element-4281" />
            </button>
          </div>}

        {/* ─── Form ────────────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4282">
          {/* ─── User search ─────────────────────────────────────── */}
          <div data-id="element-4283">
            <label className={labelClass} data-id="element-4284">1. Identificar usuario</label>

            {/* Tabs: search by email vs paste user_id */}
            <div className="flex gap-2 mb-3" data-id="element-4285">
              <button type="button" onClick={() => {
              setSearchMode('email');
              setManualError(null);
            }} className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${searchMode === 'email' ? 'bg-indigo-500/15 border border-indigo-500/40 text-indigo-300' : 'bg-[#141414] border border-white/10 text-white/50 hover:text-white'}`} data-id="element-4286">
                Buscar por correo
              </button>
              <button type="button" onClick={() => {
              setSearchMode('uuid');
              setSearchResults([]);
              setSearchDebug(null);
            }} className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${searchMode === 'uuid' ? 'bg-indigo-500/15 border border-indigo-500/40 text-indigo-300' : 'bg-[#141414] border border-white/10 text-white/50 hover:text-white'}`} data-id="element-4287">
                Pegar User ID
              </button>
            </div>

            {/* MODE A — search by email */}
            {searchMode === 'email' && <>
                <div className="relative" data-id="element-4288">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/25" data-id="element-4289" />
                  <input type="text" value={emailQuery} onChange={e => {
                setEmailQuery(e.target.value);
                if (selectedUser) setSelectedUser(null);
              }} placeholder="email@ejemplo.com o nombre" className={inputClass} disabled={isSubmitting} autoComplete="off" data-id="element-4290" />
                  {selectedUser && <button type="button" onClick={handleClearUser} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors" title="Limpiar selección" data-id="element-4291">
                      <X className="w-4 h-4" data-id="element-4292" />
                    </button>}
                </div>

                {!selectedUser && emailQuery.trim().length >= 2 && <div className="mt-2 bg-[#141414] border border-white/10 rounded-xl overflow-hidden" data-id="element-4293">
                    {searching ? <div className="px-4 py-4 flex items-center gap-2 text-white/50 text-sm" data-id="element-4294">
                        <Loader2 className="w-4 h-4 animate-spin" data-id="element-4295" />
                        Buscando…
                      </div> : searchResults.length === 0 ? <div className="px-4 py-4 text-white/40 text-sm space-y-2" data-id="element-4296">
                        <p data-id="element-4297">
                          No encontramos al usuario buscando por correo. Pasá a
                          la pestaña{' '}
                          <button type="button" onClick={() => setSearchMode('uuid')} className="font-bold text-indigo-300 underline underline-offset-2 hover:text-indigo-200" data-id="element-4298">
                            Pegar User ID
                          </button>{' '}
                          y copiá el UUID desde Supabase Auth → Users.
                        </p>
                      </div> : <ul className="divide-y divide-white/5 max-h-80 overflow-y-auto" data-id="element-4299">
                        {searchResults.map(u => <li key={u.user_id} data-id="element-4300">
                            <button type="button" onClick={() => handleSelectUser(u)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-left transition-colors" data-id="element-4301">
                              <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0" data-id="element-4302">
                                <UserIcon className="w-4 h-4 text-white/40" data-id="element-4303" />
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-4304">
                                <p className="text-white text-sm font-semibold truncate" data-id="element-4305">
                                  {u.full_name || u.email}
                                </p>
                                <p className="text-white/40 text-xs font-mono truncate" data-id="element-4306">
                                  {u.email}
                                </p>
                              </div>
                              {u.has_restaurant_access ? <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 text-[10px] font-bold uppercase tracking-wider border border-red-500/20 shrink-0" data-id="element-4307">
                                  Ya asignado
                                </span> : <ArrowRight className="w-4 h-4 text-white/30 shrink-0" data-id="element-4308" />}
                            </button>
                          </li>)}
                      </ul>}
                  </div>}
              </>}

            {/* MODE B — paste user_id directly (siempre funciona) */}
            {searchMode === 'uuid' && !selectedUser && <div className="space-y-2.5" data-id="element-4309">
                <p className="text-white/50 text-xs leading-relaxed bg-white/5 border border-white/10 rounded-lg px-3 py-2" data-id="element-4310">
                  Andá a Supabase → Authentication → Users, abrí el usuario y
                  copiá el{' '}
                  <span className="font-bold text-white" data-id="element-4311">User UID</span>. Pegalo
                  abajo. Opcionalmente agregá el correo para confirmar
                  visualmente.
                </p>
                <div className="relative" data-id="element-4312">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/25" data-id="element-4313" />
                  <input type="text" value={manualUserId} onChange={e => {
                setManualUserId(e.target.value);
                setManualError(null);
              }} placeholder="00000000-0000-0000-0000-000000000000" className={`${inputClass} font-mono text-sm`} disabled={isSubmitting || manualChecking} autoComplete="off" data-id="element-4314" />
                </div>
                <div className="relative" data-id="element-4315">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/25" data-id="element-4316" />
                  <input type="email" value={manualEmail} onChange={e => setManualEmail(e.target.value)} placeholder="email@ejemplo.com (opcional, sólo para mostrar)" className={inputClass} disabled={isSubmitting || manualChecking} autoComplete="off" data-id="element-4317" />
                </div>
                {manualError && <p className="flex items-center gap-2 text-red-400 text-xs font-medium" data-id="element-4318">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-4319" />
                    {manualError}
                  </p>}
                <button type="button" onClick={handleManualConfirm} disabled={isSubmitting || manualChecking || !manualUserId.trim()} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-xs uppercase tracking-wider bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 disabled:bg-white/5 disabled:text-white/30 disabled:cursor-not-allowed transition-colors border border-indigo-500/30 disabled:border-white/10" data-id="element-4320">
                  {manualChecking ? <>
                      <Loader2 className="w-4 h-4 animate-spin" data-id="element-4321" />
                      Verificando…
                    </> : <>
                      Usar este User ID
                      <ArrowRight className="w-4 h-4" data-id="element-4322" />
                    </>}
                </button>
              </div>}

            {/* Selected user card — shared between both modes */}
            {selectedUser && <div className={`mt-2 rounded-xl border px-4 py-3 flex items-center gap-3 ${userAlreadyAssigned ? 'bg-red-500/5 border-red-500/30' : 'bg-emerald-500/5 border-emerald-500/30'}`} data-id="element-4323">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${userAlreadyAssigned ? 'bg-red-500/15' : 'bg-emerald-500/15'}`} data-id="element-4324">
                  <Mail className={`w-4 h-4 ${userAlreadyAssigned ? 'text-red-400' : 'text-emerald-400'}`} data-id="element-4325" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-4326">
                  <p className="text-white text-sm font-bold truncate" data-id="element-4327">
                    {selectedUser.full_name || selectedUser.email}
                  </p>
                  <p className="text-white/50 text-[11px] font-mono truncate" data-id="element-4328">
                    {selectedUser.email !== '(sin email)' && <span data-id="element-4329">{selectedUser.email} · </span>}
                    <span className="opacity-70" data-id="element-4330">{selectedUser.user_id}</span>
                  </p>
                </div>
                <button type="button" onClick={handleClearUser} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors shrink-0" title="Cambiar usuario" data-id="element-4331">
                  <X className="w-4 h-4" data-id="element-4332" />
                </button>
                {userAlreadyAssigned && <span className="px-2 py-1 rounded-full bg-red-500/15 text-red-400 text-[10px] font-bold uppercase tracking-wider border border-red-500/25 shrink-0" data-id="element-4333">
                    Ya asignado
                  </span>}
              </div>}

            {userAlreadyAssigned && <p className="mt-2 flex items-center gap-2 text-red-400 text-xs font-medium" data-id="element-4334">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-4335" />
                Este usuario ya está asignado a un restaurante. Cada usuario
                sólo puede pertenecer a uno.
              </p>}
          </div>

          {/* ─── Restaurant select ───────────────────────────────── */}
          <div data-id="element-4336">
            <label className={labelClass} data-id="element-4337">2. Restaurante</label>
            <div className="relative" data-id="element-4338">
              <Store className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/25 z-10 pointer-events-none" data-id="element-4339" />
              <select value={selectedRestaurantId} onChange={e => setSelectedRestaurantId(e.target.value)} disabled={restaurantsLoading || isSubmitting || !!userAlreadyAssigned} className={`${inputClass} appearance-none cursor-pointer`} data-id="element-4340">
                <option value="" data-id="element-4341">
                  {restaurantsLoading ? 'Cargando restaurantes…' : 'Seleccioná un restaurante'}
                </option>
                {restaurants.map(r => <option key={r.id} value={r.id} className="bg-[#141414]" data-id="element-4342">
                    {r.name}
                  </option>)}
              </select>
            </div>
          </div>

          {/* ─── Role select ─────────────────────────────────────── */}
          <div data-id="element-4343">
            <label className={labelClass} data-id="element-4344">3. Rol</label>
            <div className="grid sm:grid-cols-2 gap-2.5" data-id="element-4345">
              {ASSIGNABLE_ROLES.map(r => {
              const checked = role === r.value;
              return <button key={r.value} type="button" onClick={() => setRole(r.value)} disabled={isSubmitting || !!userAlreadyAssigned} className={`text-left rounded-xl border px-4 py-3 transition-colors ${checked ? 'bg-indigo-500/10 border-indigo-500/40' : 'bg-[#141414] border-white/10 hover:border-white/25'} disabled:opacity-50 disabled:cursor-not-allowed`} data-id="element-4346">
                    <div className="flex items-center justify-between" data-id="element-4347">
                      <p className="text-white text-sm font-bold" data-id="element-4348">{r.label}</p>
                      <div className={`w-4 h-4 rounded-full border-2 ${checked ? 'border-indigo-400 bg-indigo-400' : 'border-white/30'}`} data-id="element-4349" />
                    </div>
                    <p className="text-white/40 text-xs mt-0.5 leading-snug" data-id="element-4350">
                      {r.desc}
                    </p>
                  </button>;
            })}
            </div>
          </div>

          {/* ─── Error ────────────────────────────────────────────── */}
          {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-2" data-id="element-4351">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" data-id="element-4352" />
              <p className="text-red-300 text-sm" data-id="element-4353">{error}</p>
            </div>}

          {/* ─── Submit ───────────────────────────────────────────── */}
          <button type="submit" disabled={isSubmitting || !selectedUser || !selectedRestaurantId || !role || !!userAlreadyAssigned} className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 disabled:bg-white/5 disabled:text-white/30 disabled:cursor-not-allowed transition-colors border border-indigo-500/30 disabled:border-white/10" data-id="element-4354">
            {isSubmitting ? <>
                <Loader2 className="w-4 h-4 animate-spin" data-id="element-4355" />
                Asignando…
              </> : <>
                Asignar admin
                <ArrowRight className="w-4 h-4" data-id="element-4356" />
              </>}
          </button>
        </form>
      </div>
    </div>;
}